import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  CloudArrowUpIcon,
  XMarkIcon,
  PhotoIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import usePublicConfig from '../hooks/usePublicConfig';
import { photosAPI } from '../services/photosApi';
import { getApiErrorMessage } from '../utils/apiError';

// Anshul, 2026-09-26: "photo sets ... its own Review Queue and GRIHA report" -- built here
// as its own upload modal (style copied from VideoUpload.js, which this phase does not own
// and must not edit) rather than reusing that component, since the flow itself differs:
// one title up front, a preview list with per-file remove before anything is sent, then a
// batched upload with its own progress bar instead of VideoUpload's per-file bar.
const BATCH_SIZE = 10;

// A short delay before the modal's own auto-close (mirrors VideoUpload.js's
// AUTO_CLOSE_DELAY_MS): a beat to see the finished batch before the modal closes itself.
const AUTO_CLOSE_DELAY_MS = 900;

function todayTitle() {
  const d = new Date();
  return `Photos ${d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}`;
}

function extensionOf(name) {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : '';
}

function formatFileSize(bytes) {
  if (bytes === null || bytes === undefined) return '';
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Upload one or more photos into a fresh photo set (Anshul, 2026-09-26): create the empty
 * set, add images in batches of 10 (the server accepts at most 20 per request; batching
 * comfortably under that also keeps one slow batch from blocking the whole upload), then
 * mark it complete. Cancel stops further batches -- the set stays as `UPLOADING` with
 * whatever photos made it in, deletable from the Videos list.
 */
export default function PhotoUpload({ isOpen, projectId, onClose }) {
  const queryClient = useQueryClient();
  const { config } = usePublicConfig();
  const upload = config?.upload || {};
  const allowedExtensions = (upload.photo_allowed_file_types || []).map((t) => String(t).toLowerCase());
  const allowedLabel = allowedExtensions.length > 0 ? allowedExtensions.map((e) => e.toUpperCase()).join(', ') : null;
  const maxFileBytes = typeof upload.photo_max_file_size === 'number' ? upload.photo_max_file_size : null;
  const maxPhotos = typeof upload.photo_set_max_photos === 'number' ? upload.photo_set_max_photos : null;

  const [title, setTitle] = useState(todayTitle);
  const [items, setItems] = useState([]); // {id, file, previewUrl, status, reason}
  const [isDragActive, setIsDragActive] = useState(false);
  const [phase, setPhase] = useState('form'); // 'form' | 'uploading' | 'done'
  const [sent, setSent] = useState(0);
  const [totalToSend, setTotalToSend] = useState(0);
  const fileInputRef = useRef(null);
  const nextIdRef = useRef(0);
  const cancelRef = useRef(false);
  const controllerRef = useRef(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  // The created set's id outlives `startUpload`'s own call (a Cancel, or the final
  // `complete()` failing, both leave the modal open with a way to finish or retry later --
  // see `finishIncomplete` below), so it is a ref rather than a local variable.
  const createdVideoIdRef = useRef(null);
  // True only once `photosAPI.complete` has actually succeeded for this set. Cancel and a
  // failed `complete()` both reach `phase === 'done'` without this being true, which is
  // exactly the "incomplete" state finding #4 needed a real Finish/Leave choice for,
  // instead of a Close button next to a toast that falsely promised "reopen this dialog to
  // add the rest" (reopening always starts a brand new set, per photosApi.js's own
  // create/upload/complete contract).
  const [finalized, setFinalized] = useState(false);

  useEffect(() => {
    if (isOpen) setTitle(todayTitle());
  }, [isOpen]);

  // `window.URL`, not the bare `URL` global: assessmentApi.js's `saveBlob` uses the same
  // qualified form, for the same reason -- in this Jest/jsdom setup the unqualified `URL`
  // identifier resolves to Node's own global (from the `url` module), a different object
  // from `window.URL`, so a test that mocks `window.URL.createObjectURL` would otherwise
  // never be seen by this code.
  const revokeAll = useCallback(() => {
    itemsRef.current.forEach((item) => window.URL.revokeObjectURL(item.previewUrl));
  }, []);

  // Object URLs are per-tab browser memory; revoke every one on unmount so a long session
  // opening this modal repeatedly does not leak them.
  useEffect(() => () => revokeAll(), [revokeAll]);

  const validationError = useCallback(
    (file) => {
      if (allowedExtensions.length > 0) {
        const ext = extensionOf(file.name);
        if (!allowedExtensions.includes(ext)) {
          return `"${file.name}" was not added. Unsupported file type.${allowedLabel ? ` Allowed: ${allowedLabel}.` : ''}`;
        }
      }
      if (maxFileBytes !== null && file.size > maxFileBytes) {
        return `"${file.name}" was not added. File is too large (max ${formatFileSize(maxFileBytes)}).`;
      }
      return null;
    },
    [allowedExtensions, allowedLabel, maxFileBytes]
  );

  const addFiles = useCallback(
    (fileList) => {
      const incoming = Array.from(fileList || []);
      if (!incoming.length) return;
      const rejectionMessages = [];

      // A rejected file still gets its own preview-list row, with the reason next to it
      // (VideoUpload.js's own convention for the same problem: a toast alone is easy to
      // miss, and a file that silently never appears reads as a bug, not a rejection).
      setItems((prev) => {
        const next = [...prev];
        let activeCount = next.filter((item) => item.status !== 'rejected').length;
        incoming.forEach((file) => {
          let reason = null;
          if (maxPhotos !== null && activeCount >= maxPhotos) {
            reason = `"${file.name}" was not added. This set already has the most photos one set allows (${maxPhotos}).`;
          } else {
            reason = validationError(file);
          }
          if (reason) {
            rejectionMessages.push(reason);
            next.push({
              id: `photo-${nextIdRef.current++}`,
              file,
              previewUrl: window.URL.createObjectURL(file),
              status: 'rejected',
              reason,
            });
            return;
          }
          activeCount += 1;
          next.push({
            id: `photo-${nextIdRef.current++}`,
            file,
            previewUrl: window.URL.createObjectURL(file),
            status: 'pending',
          });
        });
        return next;
      });

      if (rejectionMessages.length) {
        toast.error(rejectionMessages.join(' '), { id: 'photo-upload-validation', duration: 6000 });
      }
    },
    [validationError, maxPhotos]
  );

  const removeItem = useCallback((id) => {
    setItems((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) window.URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragActive(false);
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };
  const handleFileInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const handleClose = useCallback(() => {
    revokeAll();
    setItems([]);
    setPhase('form');
    setSent(0);
    setTotalToSend(0);
    cancelRef.current = false;
    createdVideoIdRef.current = null;
    setFinalized(false);
    onClose?.();
  }, [onClose, revokeAll]);

  const markStatus = (ids, patch) =>
    setItems((prev) => prev.map((item) => (ids.has(item.id) ? { ...item, ...patch } : item)));

  const startUpload = useCallback(async () => {
    // Pre-rejected files (wrong type, too large, over the set's own limit) stay in the
    // preview list so their reason is visible, but never go to the server.
    const uploadable = itemsRef.current.filter((item) => item.status !== 'rejected');
    if (uploadable.length === 0) return;
    cancelRef.current = false;
    setPhase('uploading');
    setSent(0);
    setTotalToSend(uploadable.length);

    let createdVideoId;
    try {
      const created = await photosAPI.createSet(projectId, { title: title.trim() || todayTitle() });
      const video = created.data?.video || created.data;
      createdVideoId = video?.id;
      if (!createdVideoId) throw new Error('The server did not return a photo set id');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not create the photo set'));
      setPhase('form');
      return;
    }
    createdVideoIdRef.current = createdVideoId;

    const batches = [];
    for (let i = 0; i < uploadable.length; i += BATCH_SIZE) {
      batches.push(uploadable.slice(i, i + BATCH_SIZE));
    }

    let sentSoFar = 0;
    for (const batch of batches) {
      if (cancelRef.current) break;
      const batchIds = new Set(batch.map((item) => item.id));
      markStatus(batchIds, { status: 'uploading' });

      const controller = new AbortController();
      controllerRef.current = controller;
      const formData = new FormData();
      batch.forEach((item) => formData.append('files', item.file));

      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await photosAPI.upload(createdVideoId, formData, undefined, {
          signal: controller.signal,
        });
        const accepted = response.data?.accepted || [];
        const rejected = response.data?.rejected || [];
        setItems((prev) =>
          prev.map((item) => {
            if (!batchIds.has(item.id)) return item;
            if (accepted.includes(item.file.name)) return { ...item, status: 'accepted' };
            const rej = rejected.find((r) => r.filename === item.file.name);
            return { ...item, status: 'rejected', reason: rej?.reason || 'Rejected by the server' };
          })
        );
        sentSoFar += accepted.length;
      } catch (err) {
        if (controller.signal.aborted) {
          markStatus(batchIds, { status: 'canceled' });
          break;
        }
        // 400: every file in this batch was rejected, per the route's own contract. The
        // detail is structured ({message, rejected: [{filename, reason}]}), not a plain
        // string, so getApiErrorMessage's formatApiErrorDetail (which only reads a string,
        // an array of {msg}, or a {msg} object) fell through to its generic fallback --
        // "Upload failed" for every file in the batch, losing the server's real per-file
        // reasons. The server rejects in the same order the batch was sent, so mapping by
        // position (rather than by filename) also gets the right reason on each item when
        // a batch carries duplicate filenames -- common from an iOS share sheet, which
        // resets its own counter and can hand several photos the same "image.jpg".
        const detailRejected = err?.response?.data?.detail?.rejected;
        if (Array.isArray(detailRejected) && detailRejected.length === batch.length) {
          const reasonByItemId = new Map(
            batch.map((item, i) => [item.id, detailRejected[i]?.reason || 'Rejected by the server'])
          );
          setItems((prev) =>
            prev.map((item) =>
              reasonByItemId.has(item.id)
                ? { ...item, status: 'rejected', reason: reasonByItemId.get(item.id) }
                : item
            )
          );
        } else {
          const reason = getApiErrorMessage(err, 'Upload failed');
          markStatus(batchIds, { status: 'rejected', reason });
        }
      }
      setSent(sentSoFar);
    }

    if (cancelRef.current) {
      // toast.error rather than the bare toast() call: this codebase's toast mock (every
      // other test file in this client) only ever stubs .success/.error, matching react-hot-
      // toast's own most-used surface -- and a canceled, incomplete set is closer to a
      // warning the auditor must act on than a success. The modal itself (not just this
      // toast) now offers the real choice -- finish with what was sent, or leave it -- since
      // reopening this dialog always starts a brand new set (photosApi.js's own
      // create/upload/complete contract), never adds to this one.
      toast.error(
        'Upload canceled. This photo set is incomplete. Use the buttons below to finish it with the photos already sent, or leave it as is.',
        { id: 'photo-upload-canceled', duration: 8000 }
      );
      queryClient.invalidateQueries(['project-videos', projectId]);
      setPhase('done');
      return;
    }

    try {
      await photosAPI.complete(createdVideoId);
      toast.success('Photo set uploaded');
      queryClient.invalidateQueries(['project-videos', projectId]);
      setFinalized(true);
      setPhase('done');
    } catch (err) {
      // The photos already accepted are NOT lost -- only this last, finishing call failed --
      // so this is the same "incomplete, needs finishing or leaving" state Cancel reaches,
      // not a dead end: the modal offers the same Finish/Leave choice below.
      toast.error(getApiErrorMessage(err, 'Could not finish the photo set'));
      setPhase('done');
    }
  }, [projectId, queryClient, title]);

  const handleCancel = useCallback(() => {
    cancelRef.current = true;
    controllerRef.current?.abort();
  }, []);

  // Retried from the modal itself after a Cancel or a failed `complete()`: the set is still
  // UPLOADING (POST .../photos/complete answers 409 otherwise), so finishing it here with
  // exactly the photos already accepted is the same call `startUpload` would have made.
  const finishIncomplete = useCallback(async () => {
    const id = createdVideoIdRef.current;
    if (!id) return;
    try {
      await photosAPI.complete(id);
      toast.success('Photo set uploaded');
      queryClient.invalidateQueries(['project-videos', projectId]);
      setFinalized(true);
      handleClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not finish the photo set'));
    }
  }, [projectId, queryClient, handleClose]);

  const hasFailure = items.some((item) => item.status === 'rejected' || item.status === 'canceled');
  // A clean run: `complete()` actually succeeded (`finalized`) and nothing was rejected or
  // canceled along the way. Distinct from `finalized` alone, since a partial-rejection batch
  // still reaches a successful `complete()` call (D21-style: some files rejected does not
  // stop the rest of the set from being finished) but is not "clean".
  const cleanSuccess = finalized && !hasFailure;

  // Closes itself once a clean, all-success run is done, giving a beat to see the finished
  // upload first (VideoUpload.js's own convention); anything else (a partial rejection, a
  // cancel, or a failed `complete()`) stays on screen since it needs the auditor's attention.
  useEffect(() => {
    if (phase !== 'done' || !cleanSuccess) return undefined;
    const timeoutId = setTimeout(handleClose, AUTO_CLOSE_DELAY_MS);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, cleanSuccess]);

  if (!isOpen) return null;

  const isBusy = phase === 'uploading';
  const pendingItems = items.filter((item) => item.status === 'pending');
  const acceptedCount = items.filter((item) => item.status === 'accepted').length;
  const rejectedItems = items.filter((item) => item.status === 'rejected');
  // The set was never finished -- Cancel stopped the loop before `complete()` was ever
  // called, or `complete()` itself failed -- and at least one photo made it to the server:
  // a real, actionable set to finish or leave, not just a bare "Close" over a set the
  // automatic `complete()` path already finished (finding #4) -- a partial-rejection batch
  // that still reached a successful `complete()` (`finalized` true) has nothing left to
  // finish, only to acknowledge. This does NOT read item statuses (no `hasFailure` here):
  // an in-flight batch can still resolve as fully accepted after Cancel was clicked (the
  // abort racing the response), leaving no item marked 'canceled' at all, so whether the
  // set was actually finished is `finalized` alone, not what happened to any one item.
  const showFinishOrLeave = phase === 'done' && !finalized && acceptedCount > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" onClick={isBusy ? undefined : handleClose} />

        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Upload photos</h3>
            <button
              type="button"
              onClick={handleClose}
              disabled={isBusy}
              className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="mb-4">
            <label htmlFor="photo-set-title" className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              id="photo-set-title"
              type="text"
              value={title}
              disabled={phase !== 'form'}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-md text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>

          {phase === 'form' && (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              role="button"
              tabIndex={0}
              className={`upload-area cursor-pointer ${isDragActive ? 'dragover' : ''}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
                aria-label="Upload photos"
              />
              <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">Drag &amp; drop photos here, or click to select files</p>
              <p className="mt-1 text-xs text-gray-400">
                {allowedLabel ? `Supports ${allowedLabel}` : 'Images'}
                {maxFileBytes !== null ? ` (max ${formatFileSize(maxFileBytes)} each` : ''}
                {maxPhotos !== null ? `${maxFileBytes !== null ? ', ' : ' ('}up to ${maxPhotos} photos)` : maxFileBytes !== null ? ')' : ''}
              </p>
            </div>
          )}

          {items.length > 0 && (
            <div className="mt-4">
              {isBusy && (
                <div className="mb-3">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${totalToSend ? Math.round((sent / totalToSend) * 100) : 0}%` }}
                      role="progressbar"
                      aria-valuenow={sent}
                      aria-valuemin={0}
                      aria-valuemax={totalToSend}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {sent} of {totalToSend} photos sent
                  </p>
                </div>
              )}
              <ul className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                {items.map((item) => (
                  <li key={item.id} className="relative">
                    <div className="relative rounded-md overflow-hidden border border-gray-200 aspect-square bg-gray-100">
                      <img src={item.previewUrl} alt={item.file.name} className="w-full h-full object-cover" />
                      {item.status === 'accepted' && (
                        <CheckCircleIcon
                          className="absolute bottom-1 right-1 h-5 w-5 text-green-500 bg-white rounded-full"
                          aria-label="Uploaded"
                        />
                      )}
                      {(item.status === 'rejected' || item.status === 'canceled') && (
                        <ExclamationCircleIcon
                          className="absolute bottom-1 right-1 h-5 w-5 text-red-500 bg-white rounded-full"
                          aria-label={item.status === 'canceled' ? 'Canceled' : 'Failed'}
                        />
                      )}
                      {phase === 'form' && (
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          aria-label={`Remove ${item.file.name}`}
                          className="absolute top-1 right-1 rounded-full bg-black/60 text-white p-0.5 hover:bg-black/80"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="mt-0.5 text-[10px] text-gray-500 truncate" title={item.file.name}>
                      {item.file.name}
                    </p>
                  </li>
                ))}
              </ul>
              {rejectedItems.length > 0 && (
                <ul className="mt-2 space-y-0.5" data-testid="photo-rejections">
                  {rejectedItems.map((item) => (
                    <li key={item.id} role="alert" className="text-xs text-red-600">
                      {item.file.name}: {item.reason}
                    </li>
                  ))}
                </ul>
              )}
              {phase === 'done' && cleanSuccess && (
                <p className="mt-2 text-xs text-green-700">{acceptedCount} photo{acceptedCount === 1 ? '' : 's'} uploaded.</p>
              )}
              {showFinishOrLeave && (
                <p className="mt-2 text-xs text-gray-600">
                  {acceptedCount} photo{acceptedCount === 1 ? '' : 's'} sent, but this set is not finished. Finish it
                  now with what was sent, or leave it here — it stays in the Videos list as an incomplete set you can
                  delete with that row's Delete button.
                </p>
              )}
            </div>
          )}

          <div className="flex justify-between items-center pt-4 mt-2">
            <span className="text-xs text-gray-400">
              {items.length > 0 ? `${items.length} photo${items.length === 1 ? '' : 's'} selected` : ''}
            </span>
            <div className="flex gap-2">
              {isBusy && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
                >
                  Cancel
                </button>
              )}
              {phase === 'form' && (
                <button
                  type="button"
                  onClick={startUpload}
                  disabled={pendingItems.length === 0}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  <PhotoIcon className="h-4 w-4 mr-2" />
                  Upload {pendingItems.length > 0 ? `${pendingItems.length} photo${pendingItems.length === 1 ? '' : 's'}` : 'photos'}
                </button>
              )}
              {phase !== 'form' && !isBusy && showFinishOrLeave && (
                <>
                  <button
                    type="button"
                    onClick={handleClose}
                    title="This photo set stays in the Videos list as an incomplete set; delete it from that row's Delete button."
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-[#F8F8FF]"
                  >
                    Leave it
                  </button>
                  <button
                    type="button"
                    onClick={finishIncomplete}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Finish with the {acceptedCount} photo{acceptedCount === 1 ? '' : 's'} sent
                  </button>
                </>
              )}
              {phase !== 'form' && !isBusy && !showFinishOrLeave && (
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-[#F8F8FF]"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
